import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";

import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import { join } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { getDomainWithSubdomain } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { useSimilarSites } from "app/services/sitesService/hooks/useSimilarSites";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import type { ISerializable } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import { dedupRecent } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import type { IRecentObject } from "app/@types/userdata";

import { autocompleteStates } from "../WebsiteQueryBarTypes";

import type { IChosenItem, IChosenItemParams } from "./WebsitePageQueryBarUtils";
import { getChosenPageItems } from "./WebsitePageQueryBarUtils";
import type { ISimilarSite } from "./WebsiteQueryBar";

interface IUseWebsitePageQueryBarDataProps {
    filterRecentsBy?: StateOrName;
    setChosenItems: Dispatch<SetStateAction<IChosenItem[]>>;
    setSimilarSites: Dispatch<SetStateAction<any[]>>;
    setListItemOrder: Dispatch<SetStateAction<string[]>>;
}

export const useWebsitePageQueryBarData = ({
    filterRecentsBy,
    setChosenItems,
    setSimilarSites,
    setListItemOrder,
}: IUseWebsitePageQueryBarDataProps) => {
    const [recents, setRecents] = useState<autocompleteStates[] | ISerializable[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const { params } = useCurrentStateAndParams();

    const pagesParam = useMemo<IChosenItemParams | null>(() => {
        if (!params.pageFilter) {
            return null;
        }
        try {
            return JSON.parse(params.pageFilter) as IChosenItemParams;
        } catch {
            return null;
        }
    }, [params.pageFilter]);

    const domain = useMemo<string | null>(() => {
        if (!pagesParam || pagesParam.length === 0) {
            return null;
        }
        try {
            const pages = getChosenPageItems(pagesParam);
            return getDomainWithSubdomain(pages[0].domain, pages[0].subdomain);
        } catch {
            return null;
        }
    }, [pagesParam]);

    const { data: similarSitesData } = useSimilarSites(
        domain || "",
        {
            limit: 20,
            country: params.country,
            webSource: params.webSource,
        },
        {
            enabled: !!domain,
        },
    );

    const setSitesData = async (urlList: IChosenItemParams) => {
        const sitesData = getChosenPageItems(urlList);
        setChosenItems(sitesData);
        setListItemOrder(sitesData.map((item) => item.url));
        return sitesData;
    };

    const getAndFilterRecentsAnalysis = async () => {
        try {
            let recentItems: ISerializable[] = [];
            if (filterRecentsBy) {
                recentItems = await getRecentsByParentState(filterRecentsBy, false);
            } else {
                recentItems = await getRecentsAnalysis("website", false);
            }
            const recentItemsDeduped = dedupRecent(recentItems, (recent: IRecentObject) => {
                if (recent.data?.comparedItems?.length > 0) {
                    const normalizedMainItem = recent.data?.mainItem?.toLowerCase();
                    const normalizedComparedItems = recent.data?.comparedItems
                        ?.map((item) => item.toLowerCase())
                        .sort();
                    const serializedItems = join([
                        normalizedMainItem,
                        ...(normalizedComparedItems ? normalizedComparedItems : []),
                    ]);
                    return serializedItems;
                }
                const recentPageFilter: IChosenItemParams = recent.data?.params?.pageFilter?.length
                    ? JSON.parse(recent.data?.params?.pageFilter)
                    : [];
                if (
                    recentPageFilter[0] &&
                    recentPageFilter[0]?.url?.toLowerCase() !== recent.data?.mainItem?.toLowerCase()
                ) {
                    return `${recent.data?.mainItem?.toLowerCase()}_${recentPageFilter[0]?.url?.toLowerCase()}`;
                }
                return recent.data?.mainItem?.toLowerCase();
            });
            if (recentItemsDeduped.length === 0) {
                recentItemsDeduped[0] =
                    autocompleteStates.NO_DATA_RECENTS as unknown as (typeof recentItemsDeduped)[number];
            }
            return recentItemsDeduped;
        } catch (e) {
            Sentry.errorTracker({
                e,
                message: "Error fetching recents - WebsiteQueryBar",
                level: "error",
                params: {
                    extra: { filterRecentsBy },
                },
            });
            return [autocompleteStates.ERROR];
        }
    };

    useEffect(() => {
        (async () => {
            const recentItems = await getAndFilterRecentsAnalysis();
            setRecents(recentItems);
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (!pagesParam) {
            return;
        }

        (async () => {
            try {
                await setSitesData(pagesParam);
                const recentItems = await getAndFilterRecentsAnalysis();
                setRecents(recentItems);
            } catch (e) {
                Sentry.errorTracker({
                    e,
                    message: "Error setting sites data - WebsiteQueryBar",
                    level: "error",
                    params: {
                        extra: { pagesParam },
                    },
                });
            }
        })();
    }, [pagesParam]);

    useEffect(() => {
        if (!similarSitesData) {
            return;
        }

        try {
            const sites =
                similarSitesData.length === 0
                    ? [autocompleteStates.NO_DATA_SIMILARSITES as unknown as ISimilarSite]
                    : similarSitesData;
            setSimilarSites(sites);
        } catch (e) {
            Sentry.errorTracker({
                e,
                message: "Error setting similar sites - WebsiteQueryBar",
                level: "error",
                params: {
                    extra: { similarSitesData },
                },
            });
            setSimilarSites([autocompleteStates.ERROR]);
        }
    }, [similarSitesData, setSimilarSites]);

    return { recents, isLoading };
};
